import { Tab } from "@mui/base/Tab";
import { TabPanel } from "@mui/base/TabPanel";
import { Tabs } from "@mui/base/Tabs";
import { TabsList } from "@mui/base/TabsList";
import { ReactNode, useState } from "react";
import { Navigate, Outlet, useParams } from "react-router-dom";

import { AiVisibilityEngineStatus } from "~/components/siteSpeed/AiVisibilityEngineStatus.tsx";
import SystemConfigTable from "~/components/siteSpeed/dns/SystemConfigTable";
import { UserConfigTable } from "~/components/siteSpeed/dns/UserConfigTable";
import { useConfigurationBreadcrumbs } from "~/components/siteSpeed/useVisibilityEngineRoutes";
import Breadcrumbs from "~/components/ui/breadcrumbs/Breadcrumbs";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { useTeam } from "~/contexts/TeamProvider.tsx";
import { WizardProvider } from "~/contexts/WizardContext";
import { ALLI_VERIFICATION_NAME } from "~/helpers/consts";
import { usePageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";
import { getBrandName } from "~/utils/white-label";

import Spinner from "../ui/spinner/Spinner";
import styles from "./SiteSpeed.module.scss";
import { useDomainWithDns } from "./hooks";
import Certificate from "./wizzard/Certificate";
import { Configure } from "./wizzard/Configure";
import { Confirmation } from "./wizzard/Confirmation";
import Install from "./wizzard/Install";
import Nameservers from "./wizzard/Nameservers";
import Routing from "./wizzard/Routing";

const SiteSpeed = () => {
  usePageTitle(PAGE_TITLES.aiVisibilityEngine);

  const { domainId } = useParams<{ domainId: string }>();

  if (!domainId) {
    throw Error("No domain id provided");
  }

  const { team } = useTeam();
  const breadcrumbs = useConfigurationBreadcrumbs();

  const [activeTab, setActiveTab] = useState(0);
  const { data, showInitialLoader } = useDomainWithDns(domainId);

  const integrationType = data?.integration_type;
  const isFullIntegration = integrationType === "full";
  const isEdgeIntegration = integrationType === "edge";
  const isPluginIntegration = integrationType === "plugin";

  if (team && !team.subscription?.price?.product?.prerendering) {
    return <Navigate to={routes.home()} replace />;
  }

  if (showInitialLoader || !data) {
    return <Spinner />;
  }

  const verificationData =
    data.system_config?.filter(
      (record) =>
        typeof record?.[0] === "string" &&
        record[0].startsWith(ALLI_VERIFICATION_NAME),
    ) ?? [];

  let tabs: { label: string; content: ReactNode }[];

  if (isPluginIntegration) {
    tabs = [
      { label: "Installation", content: <Install data={verificationData} /> },
      { label: "Configuration", content: <Configure domain={data} /> },
    ];
  } else if (isEdgeIntegration) {
    tabs = [{ label: "Confirmation", content: <Confirmation /> }];
  } else {
    tabs = [
      {
        label: isFullIntegration ? "DNS Records" : "Origins",
        content: (
          <>
            {data.user_config?.length ? (
              <UserConfigTable domain={data} />
            ) : null}
            {isFullIntegration && data.system_config?.length ? (
              <div className={styles.systemConfig}>
                <div className={styles.siteSpeedCardTitle}>
                  Managed by {getBrandName()}
                </div>
                <SystemConfigTable domain={data} />
              </div>
            ) : null}
          </>
        ),
      },
      { label: "Certificate", content: <Certificate /> },
      isFullIntegration
        ? { label: "Nameservers", content: <Nameservers domain={data} /> }
        : { label: "Routing", content: <Routing domain={data} /> },
    ];
  }

  return (
    <WizardProvider domain={data}>
      <div className={styles.page}>
        <h1 className={styles.title}>{PAGE_TITLES.aiVisibilityEngine}</h1>
        {breadcrumbs && <Breadcrumbs fakeBreadcrumbs={breadcrumbs} />}
        <AiVisibilityEngineStatus
          domain={data}
          className={breadcrumbs ? styles.status : undefined}
        />
        <div className={styles.siteSpeedCard}>
          <div className={styles.siteSpeedCardTitle}>{data?.name}</div>
          {tabs.length === 1 ? (
            // @ts-expect-error TS2339 non-existing className
            <div className={styles.tabPanel}>{tabs[0].content}</div>
          ) : (
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value as number)}
              // @ts-expect-error TS2339 non-existing className
              className={styles.tabs}
            >
              <TabsList className={styles.tabsList}>
                {tabs.map((tab, index) => (
                  <Tab
                    key={tab.label}
                    value={index}
                    className={styles.tabButton}
                  >
                    {tab.label}
                  </Tab>
                ))}
              </TabsList>
              {tabs.map((tab, index) => (
                <TabPanel
                  key={tab.label}
                  value={index}
                  // @ts-expect-error TS2339 non-existing className
                  className={styles.tabPanel}
                >
                  {tab.content}
                </TabPanel>
              ))}
            </Tabs>
          )}
          <Outlet />
        </div>
      </div>
    </WizardProvider>
  );
};

export default SiteSpeed;
